import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Percent, RefreshCw, AlertTriangle, HelpCircle } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LoadingRows } from '@/components/common/LoadingState';
import { ErrorState } from '@/components/common/ErrorState';
import { inventoryService } from '@/services/inventory';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const rate = (v: number | null) => (v == null ? '—' : `${v}%`);

/**
 * Sync HSN rates — re-applies the platform HSN→GST master to the
 * tenant's taxable catalog. Preview-first: the operator sees every
 * proposed change and ticks which to apply, so deliberate manual
 * overrides are never silently overwritten. Items with no HSN code or
 * an HSN the master doesn't know are surfaced separately to fix.
 */
export function SyncHsnRatesDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const auditQuery = useQuery({
    queryKey: ['inventory', 'hsn-rate-audit'],
    queryFn: () => inventoryService.hsnRateAudit(),
    enabled: open,
  });

  const audit = auditQuery.data;

  // Default every drifted item to selected once the audit lands.
  useEffect(() => {
    if (audit) setSelected(new Set(audit.wouldChange.map((i) => i.id)));
  }, [audit]);

  const apply = useMutation({
    mutationFn: () => inventoryService.reapplyHsnRates([...selected]),
    onSuccess: (res) => {
      toast.success(
        `${res.updated} item${res.updated === 1 ? '' : 's'} updated` +
          (res.skipped.length ? `, ${res.skipped.length} skipped` : ''),
      );
      qc.invalidateQueries({ queryKey: ['inventory'] });
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not apply rates'),
  });

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allSelected =
    !!audit && audit.wouldChange.length > 0 && selected.size === audit.wouldChange.length;
  const toggleAll = () =>
    setSelected(
      allSelected ? new Set() : new Set(audit!.wouldChange.map((i) => i.id)),
    );

  const nothingToDo =
    !!audit &&
    audit.wouldChange.length === 0 &&
    audit.noHsn.length === 0 &&
    audit.noMatch.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Percent className="h-4 w-4 text-muted-foreground" />
            Sync HSN rates
          </DialogTitle>
        </DialogHeader>

        {auditQuery.isLoading ? (
          <LoadingRows rows={4} cols={2} />
        ) : auditQuery.error ? (
          <ErrorState error={auditQuery.error} onRetry={() => auditQuery.refetch()} />
        ) : nothingToDo ? (
          <div className="rounded-lg border border-dashed border-border/70 px-4 py-8 text-center">
            <Percent className="mx-auto h-5 w-5 text-muted-foreground/50" />
            <div className="mt-2 text-[12.5px] text-muted-foreground">
              Every taxable item already matches the HSN→GST master. Nothing to
              sync.
            </div>
          </div>
        ) : audit ? (
          <div className="space-y-4">
            <p className="text-[12.5px] text-muted-foreground">
              Re-applies the platform HSN→GST master to your taxable catalog.
              Already-issued invoices keep their snapshot — only the catalog is
              updated.
            </p>

            {/* Would-change — the actionable, selectable list. */}
            {audit.wouldChange.length > 0 && (
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Rate changes ({audit.wouldChange.length})
                  </div>
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="text-[11.5px] text-primary hover:underline"
                  >
                    {allSelected ? 'Deselect all' : 'Select all'}
                  </button>
                </div>
                <div className="max-h-64 divide-y divide-border/60 overflow-y-auto rounded-lg border border-border/70">
                  {audit.wouldChange.map((it) => (
                    <label
                      key={it.id}
                      className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-muted/30"
                    >
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 accent-primary"
                        checked={selected.has(it.id)}
                        onChange={() => toggle(it.id)}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="muted" className="font-mono text-[10px]">
                            {it.sku}
                          </Badge>
                          <span className="truncate text-[12.5px] text-foreground">
                            {it.name}
                          </span>
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          HSN {it.hsnCode} · {it.description}
                        </div>
                      </div>
                      <div className="shrink-0 font-mono text-[12px] tabular-nums">
                        <span className="text-muted-foreground line-through">
                          {rate(it.intraStateTaxRate)}
                        </span>
                        <span className="mx-1.5 text-muted-foreground">→</span>
                        <span className="font-medium text-success">
                          {it.suggestedRate}%
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Untick any row whose rate you set deliberately — e.g. a
                  reduced slab for low-value goods.
                </p>
              </div>
            )}

            {/* No HSN — informational, needs a manual fix. */}
            {audit.noHsn.length > 0 && (
              <div className="rounded-md border border-warning/30 bg-warning/[0.06] px-3 py-2">
                <div className="flex items-center gap-1.5 text-[12px] font-medium text-foreground">
                  <AlertTriangle className="h-3.5 w-3.5 text-warning" />
                  No HSN code ({audit.noHsn.length})
                </div>
                <p className="mt-0.5 text-[11.5px] text-muted-foreground">
                  These taxable items have no HSN code, so no rate can be
                  suggested. Open each item and add an HSN code (the rate then
                  auto-fills) or set the rate manually.
                </p>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {audit.noHsn.map((it) => (
                    <Badge key={it.id} variant="outline" className="font-mono text-[10px]">
                      {it.sku}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* HSN present but not in the master. */}
            {audit.noMatch.length > 0 && (
              <div className="rounded-md border border-border/70 bg-muted/20 px-3 py-2">
                <div className="flex items-center gap-1.5 text-[12px] font-medium text-foreground">
                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground" />
                  HSN not in master ({audit.noMatch.length})
                </div>
                <p className="mt-0.5 text-[11.5px] text-muted-foreground">
                  These items carry an HSN code the master doesn't recognise.
                  Ask the platform team to add the code to the HSN→GST master,
                  then sync again.
                </p>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {audit.noMatch.map((it) => (
                    <Badge key={it.id} variant="outline" className="font-mono text-[10px]">
                      {it.sku} · {it.hsnCode}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {!!audit && audit.wouldChange.length > 0 && (
            <Button
              onClick={() => apply.mutate()}
              disabled={apply.isPending || selected.size === 0}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              {apply.isPending
                ? 'Applying…'
                : `Apply to ${selected.size} item${selected.size === 1 ? '' : 's'}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
